import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Card, Empty, Select } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import { LuDownload } from 'react-icons/lu';
import { MdAdd } from 'react-icons/md';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

import Loader from '../ReusableComponents/Loader';
import SearchInput from '../ReusableComponents/SearchInput';
import TmFilter from '../ReusableComponents/TmFilter';
import TmMasterTable from '../ReusableComponents/TmMasterTable';
import SkillManagerModal from '../SkillMatrix/SkillManagerModal';
import SkillMatrixApi from '../../Api/SkillMatrixApi';
import EmployeesReviewApi from '../../Api/EmployeesReviewApi';
import { toastError } from '../Toast/ToastComponent';
import { MONTHS } from '../../constant/constant';
import { generateYears } from '../../ReusableFn/reusableFn';
import {
  injectNativeColumnChart,
  PLACEHOLDER_PNG_BASE64,
} from './injectExcelChart';

import style from '../../Styles/Report.module.css';
import {
  getSkillMatrix,
  getNotificationHistory,
  getAttendance,
  getDepartmentReport,
  getUserReport,
  getSurveyReport,
  getVariance,
} from '../../store/reducers/actionPermission';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const CURRENT_MONTH = dayjs().format('MMMM');
const CURRENT_YEAR = dayjs().year();

function initialsFromName(name) {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const RATING_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({
  value: n,
  label: String(n),
}));

function RatingCellEditable({
  value,
  employeeId,
  skillKey,
  canEdit,
  onCommit,
}) {
  const [editing, setEditing] = useState(false);
  let v = Number(value);
  if (Number.isNaN(v)) v = 0;
  const toneClass = v ? style[`rating${v}`] || '' : '';

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    if (!canEdit) return;
    setEditing(true);
  };

  const commit = (next) => {
    let n = Number(next);
    if (Number.isNaN(n)) n = 1;
    n = Math.min(5, Math.max(1, n));
    onCommit(employeeId, skillKey, n);
    setEditing(false);
  };

  if (editing) {
    return (
      <Select
        size="small"
        style={{ width: 52 }}
        value={v}
        options={RATING_OPTIONS}
        autoFocus
        open
        getPopupContainer={() => document.body}
        onChange={(val) => commit(val)}
        onOpenChange={(open) => {
          if (!open) setEditing(false);
        }}
      />
    );
  }

  return (
    <div
      className={`${style.ratingBox} ${toneClass}`}
      aria-label={`Rating ${v} out of 5`}
      onDoubleClick={handleDoubleClick}
      title={canEdit ? 'Double-click to edit' : undefined}
      style={{ cursor: canEdit ? 'pointer' : 'default' }}
    >
      {v || '--'}
    </div>
  );
}

function useCompetencyMatrixRouteAccess() {
  const skillMatrix = useSelector(getSkillMatrix);
  const notificationHistory = useSelector(getNotificationHistory);
  const attendance = useSelector(getAttendance);
  const departmentReport = useSelector(getDepartmentReport);
  const userReport = useSelector(getUserReport);
  const surveyReport = useSelector(getSurveyReport);
  const variance = useSelector(getVariance);

  const slices = useMemo(
    () => [
      skillMatrix,
      notificationHistory,
      attendance,
      departmentReport,
      userReport,
      surveyReport,
      variance,
    ],
    [
      skillMatrix,
      notificationHistory,
      attendance,
      departmentReport,
      userReport,
      surveyReport,
      variance,
    ],
  );

  const permissionKnown = slices.some((p) => p !== undefined);
  const canAccess = slices.some((p) => p?.checked);

  return { permissionKnown, canAccess };
}

const CompetencyMatrixComp = () => {
  const navigate = useNavigate();

  const permission = useSelector(getSkillMatrix);
  const { permissionKnown, canAccess } = useCompetencyMatrixRouteAccess();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filterArr, setFilterArr] = useState({
    search: '',
    month: CURRENT_MONTH,
    year: CURRENT_YEAR,
  });
  const filterArrRef = useRef(filterArr);
  const [searchInputKey, setSearchInputKey] = useState(0);

  const yearOptions = useMemo(() => generateYears() || [], []);

  const competencyMatrixFilterArray = useMemo(
    () => [
      {
        options: MONTHS,
        placeholder: 'Month',
        controlName: 'month',
        input: 'select',
        defaultValue: CURRENT_MONTH,
        col: 2,
        size: 'small',
      },
      {
        options: yearOptions,
        placeholder: 'Year',
        controlName: 'year',
        input: 'select',
        defaultValue: CURRENT_YEAR,
        col: 2,
        size: 'small',
      },
      {
        order: 1,
        col: 'auto',
        className: 'ms-auto pe-0',
        input: 'button',
        type: 'dashed',
        htmlType: 'button',
        text: 'Reset',
      },
    ],
    [yearOptions],
  );

  const [buttonPermission, setButtonPermissions] = useState({
    add: true,
    edit: true,
    delete: false,
    view: true,
  });

  const [skills, setSkills] = useState([]);
  const [skillModalOpen, setSkillModalOpen] = useState(false);

  useEffect(() => {
    filterArrRef.current = filterArr;
  }, [filterArr]);

  const visibleSkills = useMemo(
    () => skills.filter((s) => s.selected ?? !!s.activeStatus),
    [skills],
  );

  const mergeSkillsFromManagerReviewResult = useCallback((reviewId, result) => {
    const populated = result?.manager_review_populated;
    if (Array.isArray(populated)) {
      const skillsObj = {};
      populated
        .filter((s) => s.activeStatus)
        .forEach((skill) => {
          skillsObj[String(skill.id)] = skill.value ?? 0;
        });
      setData((prev) =>
        prev.map((emp) =>
          emp.id === reviewId ? { ...emp, skills: skillsObj } : emp,
        ),
      );
      return;
    }
    const ratingArr = result?.manager_review?.rating;
    if (Array.isArray(ratingArr) && ratingArr.length > 0) {
      setData((prev) =>
        prev.map((emp) => {
          if (emp.id !== reviewId) return emp;
          const next = { ...(emp.skills || {}) };
          ratingArr.forEach((r) => {
            if (r && r.id != null) next[String(r.id)] = Number(r.value) || 0;
          });
          return { ...emp, skills: next };
        }),
      );
    }
  }, []);

  const updateSkillRating = useCallback(
    async (employeeId, skillKey, newValue) => {
      let previousValue = 0;

      setData((prev) =>
        prev.map((emp) => {
          if (emp.id !== employeeId) return emp;
          previousValue = Number(emp.skills?.[skillKey]) || 0;
          return {
            ...emp,
            skills: { ...(emp.skills || {}), [skillKey]: newValue },
          };
        }),
      );

      const skillId = Number(skillKey);
      if (Number.isNaN(skillId)) {
        toastError('Invalid skill.');
        return;
      }

      const payload = {
        review_id: employeeId,
        rating: [{ id: skillId, value: newValue }],
      };

      try {
        const res =
          await EmployeesReviewApi.updateManagerReviewRatings(payload);
        const result = res?.data?.result;
        if (result) {
          mergeSkillsFromManagerReviewResult(employeeId, result);
        }
      } catch (error) {
        console.error('Error updating manager review ratings:', error);
        toastError(
          error?.response?.data?.message || 'Failed to save rating. Try again.',
        );
        setData((prev) =>
          prev.map((emp) =>
            emp.id === employeeId
              ? {
                  ...emp,
                  skills: {
                    ...(emp.skills || {}),
                    [skillKey]: previousValue,
                  },
                }
              : emp,
          ),
        );
      }
    },
    [mergeSkillsFromManagerReviewResult],
  );

  const canEditRating = buttonPermission.edit;

  const competencyMatrixHeaders = useMemo(() => {
    const employeeCol = {
      field: 'employee_meta',
      headerName: 'EMPLOYEE',
      className: style.skillMatrixThEmployee,
      render: (_, row) => (
        <div className={style.employeeCell}>
          <div className={style.employeeAvatar} aria-hidden>
            {initialsFromName(row.name)}
          </div>
          <div className={style.employeeMeta}>
            <p className={style.employeeName}>
              {row.name} <span className={style.employeeId}>{row.empCode}</span>
            </p>

            <div className={style.employeeTags}>
              {(row.tags || []).map((tag) => (
                <span key={tag} className={style.employeeTag}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      ),
    };
    const skillCols = visibleSkills.map((c) => ({
      field: c.key,
      headerName: c.label,
      align: 'center',
      className: style.skillMatrixTdRating,
      render: (val, row) => (
        <RatingCellEditable
          value={val}
          employeeId={row.id}
          skillKey={c.key}
          canEdit={canEditRating}
          onCommit={updateSkillRating}
        />
      ),
    }));

    const avgCol = {
      field: 'average',
      headerName: 'Average',
      align: 'center',
      className: style.skillMatrixTdRating,
      render: (_, row) => {
        const values = visibleSkills
          .map((s) => Number(row.skills?.[s.key] || 0))
          .filter((v) => v > 0);
        if (values.length === 0) return <span>--</span>;
        const avg = values.reduce((a, b) => a + b, 0) / values.length;
        return <span className={style.avgValue}>{avg.toFixed(1)}</span>;
      },
    };

    return [employeeCol, ...skillCols, avgCol];
  }, [visibleSkills, canEditRating, updateSkillRating]);

  const normalizeSkillRow = useCallback(
    (s) => ({
      ...s,
      key: String(s.id),
      label: s.skill_name,
      selected: !!s.activeStatus,
    }),
    [],
  );

  const loadMatrixData = useCallback(
    async (month, year) => {
      try {
        setLoading(true);

        const skillResponse = await SkillMatrixApi.getList({});
        const skillList = Array.isArray(skillResponse?.data?.result)
          ? skillResponse.data.result
          : [];
        setSkills(skillList.map(normalizeSkillRow));

        const reviewResponse = await EmployeesReviewApi.getAllUserReviews({
          month,
          year,
        });
        const reviews = Array.isArray(reviewResponse?.data?.result)
          ? reviewResponse.data.result
          : [];

        const employeeRows = reviews.map((review) => {
          const user = review.user || {};
          const userName = [user.first_name, user.last_name]
            .filter(Boolean)
            .join(' ');

          const populatedSkills = Array.isArray(review.manager_review_populated)
            ? review.manager_review_populated.filter((s) => s.activeStatus)
            : [];

          const skillsObj = {};
          populatedSkills.forEach((skill) => {
            skillsObj[String(skill.id)] = skill.value ?? 0;
          });

          return {
            id: review.review_id,
            user_id: review.user_id ?? user.id ?? null,
            name: userName || '--',
            empCode: user.employee_code || `EMP${user.id || ''}`,
            tags: [review.designation_name, review.department_name].filter(
              Boolean,
            ),
            skills: skillsObj,
          };
        });

        setData(employeeRows);
      } catch (error) {
        console.error('Error loading competency matrix:', error);
        toastError(
          error?.response?.data?.message || 'Failed to load competency matrix.',
        );
        setSkills([]);
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [normalizeSkillRow],
  );

  useEffect(() => {
    if (permissionKnown && !canAccess) {
      navigate('/');
    }
  }, [permissionKnown, canAccess, navigate]);

  useEffect(() => {
    if (permission && permission.actions) {
      const obj = {
        add: false,
        edit: false,
        delete: false,
        view: false,
      };
      permission.actions.forEach((el) => {
        obj[el.statename.toLowerCase()] = el.checked;
      });
      setButtonPermissions(obj);
    }
  }, [permission]);

  useEffect(() => {
    loadMatrixData(filterArr.month, filterArr.year);
  }, [filterArr.month, filterArr.year, loadMatrixData]);

  const filteredRows = useMemo(() => {
    const q = filterArr.search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((row) => {
      const inName = row.name.toLowerCase().includes(q);
      const inId = row.empCode.toLowerCase().includes(q);
      const inTags = row.tags.some((t) => t.toLowerCase().includes(q));
      return inName || inId || inTags;
    });
  }, [data, filterArr.search]);

  const tableRows = useMemo(
    () =>
      filteredRows.map((r) => ({
        ...r,
        employee_meta: true,
        ...r.skills,
      })),
    [filteredRows],
  );

  /**
   * Per-skill average across the currently-filtered employees.
   * Zero / blank ratings are excluded so the average reflects only rated employees.
   */
  const skillAverages = useMemo(() => {
    return visibleSkills.map((skill) => {
      const values = filteredRows
        .map((row) => Number(row.skills?.[skill.key]) || 0)
        .filter((v) => v > 0);
      const avg =
        values.length > 0
          ? values.reduce((a, b) => a + b, 0) / values.length
          : 0;
      return {
        key: skill.key,
        label: skill.label,
        average: avg,
        ratedCount: values.length,
      };
    });
  }, [visibleSkills, filteredRows]);

  const summaryRows = useMemo(
    () =>
      skillAverages.map((s) => ({
        ...s,
        averageDisplay: s.average ? s.average.toFixed(2) : '0.00',
      })),
    [skillAverages],
  );

  const summaryColumns = useMemo(
    () => [
      {
        field: 'label',
        headerName: 'List Of Skills',
        render: (val) => <span className={style.summarySkillName}>{val}</span>,
      },
      {
        field: 'averageDisplay',
        headerName: 'Average Skills',
        align: 'center',
        render: (val) => <span className={style.avgValue}>{val}</span>,
      },
    ],
    [],
  );

  const chartData = useMemo(
    () => ({
      labels: skillAverages.map((s) => s.label),
      datasets: [
        {
          label: 'Average Skills',
          data: skillAverages.map((s) => Number(s.average.toFixed(2))),
          backgroundColor: '#5B9BD5',
          hoverBackgroundColor: '#3f7fb8',
          borderRadius: 4,
          maxBarThickness: 48,
        },
      ],
    }),
    [skillAverages],
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: {
          display: true,
          text: 'Average Skills',
          color: '#1f2937',
          font: { size: 16, weight: '600' },
          padding: { top: 4, bottom: 12 },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => `Avg: ${Number(ctx.parsed.y).toFixed(2)}`,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          suggestedMax: 5,
          ticks: { stepSize: 0.5 },
          grid: { color: '#eef0f4' },
        },
        x: {
          ticks: { autoSkip: false, maxRotation: 30, minRotation: 0 },
          grid: { display: false },
        },
      },
    }),
    [],
  );

  const handleSearchChange = (value) => {
    setFilterArr((prev) => ({ ...prev, search: value }));
  };

  const handleFilterChange = (value) => {
    const merged = { ...filterArrRef.current, ...value };
    setFilterArr(merged);
    if (value && Object.prototype.hasOwnProperty.call(value, 'search')) {
      setSearchInputKey((k) => k + 1);
    }
  };

  const openSkillModal = useCallback(() => {
    setSkillModalOpen(true);
  }, []);

  const closeSkillModal = useCallback(() => {
    setSkillModalOpen(false);
  }, []);

  const handleSkillsSaved = useCallback(
    (savedSkills) => {
      const normalized = (savedSkills || []).map(normalizeSkillRow);
      setSkills(normalized);

      setData((prev) =>
        prev.map((emp) => {
          const nextSkills = { ...(emp.skills || {}) };
          const allowed = new Set(normalized.map((s) => s.key));
          normalized.forEach((s) => {
            if (nextSkills[s.key] === undefined) nextSkills[s.key] = 0;
          });
          Object.keys(nextSkills).forEach((k) => {
            if (!allowed.has(k)) delete nextSkills[k];
          });
          return { ...emp, skills: nextSkills };
        }),
      );
    },
    [normalizeSkillRow],
  );

  const handleDownload = useCallback(async () => {
    if (filteredRows.length === 0) {
      toastError('No data to download.');
      return;
    }

    const RATING_COLORS = {
      1: 'FFFF6B6B',
      2: 'FFFFC000',
      3: 'FF92D050',
      4: 'FF00B050',
      5: 'FF385723',
    };
    const RATING_FONT_COLORS = {
      1: 'FFFFFFFF',
      2: 'FF000000',
      3: 'FF000000',
      4: 'FFFFFFFF',
      5: 'FFFFFFFF',
    };

    const thinBorder = {
      top: { style: 'thin', color: { argb: 'FF000000' } },
      left: { style: 'thin', color: { argb: 'FF000000' } },
      bottom: { style: 'thin', color: { argb: 'FF000000' } },
      right: { style: 'thin', color: { argb: 'FF000000' } },
    };

    const workbook = new ExcelJS.Workbook();

    /* ----------------------- Main matrix sheet ----------------------- */
    const worksheet = workbook.addWorksheet('Competency Matrix');

    const baseColumns = [
      { header: 'S.No', key: 'sno', width: 6 },
      { header: 'Employee ID', key: 'empCode', width: 14 },
      { header: 'Employee Name', key: 'name', width: 26 },
      { header: 'Department', key: 'department', width: 22 },
      { header: 'Designation', key: 'designation', width: 26 },
    ];

    const skillColumns = visibleSkills.map((s) => ({
      header: s.label,
      key: `skill_${s.key}`,
      width: 6,
    }));

    const avgColumn = { header: 'Average', key: 'average', width: 12 };

    worksheet.columns = [...baseColumns, ...skillColumns, avgColumn];

    filteredRows.forEach((row, index) => {
      const skillValues = visibleSkills.map(
        (s) => Number(row.skills?.[s.key]) || 0,
      );
      const nonZero = skillValues.filter((v) => v > 0);
      const avg =
        nonZero.length > 0
          ? nonZero.reduce((a, b) => a + b, 0) / nonZero.length
          : 0;

      const rowData = {
        sno: index + 1,
        empCode: row.empCode || '',
        name: row.name || '',
        department: row.tags?.[1] || '',
        designation: row.tags?.[0] || '',
        average: avg ? Number(avg.toFixed(2)) : '--',
      };
      visibleSkills.forEach((s, i) => {
        rowData[`skill_${s.key}`] = skillValues[i] || '';
      });

      const dataRow = worksheet.addRow(rowData);
      dataRow.height = 22;

      dataRow.eachCell((cell, colNumber) => {
        cell.border = thinBorder;
        cell.font = {
          name: 'Calibri',
          size: 11,
          color: { argb: 'FF000000' },
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };

        const isSkillCol =
          colNumber > baseColumns.length &&
          colNumber <= baseColumns.length + visibleSkills.length;

        if (isSkillCol) {
          const value = Number(cell.value);
          if (value >= 1 && value <= 5) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: RATING_COLORS[value] },
            };
            cell.font = {
              name: 'Calibri',
              size: 11,
              bold: true,
              color: { argb: RATING_FONT_COLORS[value] },
            };
          }
        }

        if (cell._column?._key === 'name') {
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
        }
      });
    });

    /** Footer: merged label + column averages (aligned under skill / Average columns) */
    const overallMeanAcrossEmployees = (() => {
      const means = filteredRows
        .map((row) => {
          const skillValues = visibleSkills.map(
            (s) => Number(row.skills?.[s.key]) || 0,
          );
          const nonZero = skillValues.filter((v) => v > 0);
          return nonZero.length > 0
            ? nonZero.reduce((a, b) => a + b, 0) / nonZero.length
            : null;
        })
        .filter((m) => m != null);
      return means.length > 0
        ? means.reduce((a, b) => a + b, 0) / means.length
        : 0;
    })();

    const summaryRowIndex = filteredRows.length + 2;
    const summaryRowData = {
      sno: '',
      empCode: '',
      name: '',
      department: '',
      designation: '',
    };
    visibleSkills.forEach((s) => {
      const sa = skillAverages.find((x) => x.key === s.key);
      summaryRowData[`skill_${s.key}`] =
        sa && sa.average > 0 ? Number(sa.average.toFixed(8)) : '';
    });
    summaryRowData.average =
      overallMeanAcrossEmployees > 0
        ? Number(overallMeanAcrossEmployees.toFixed(8))
        : '';

    worksheet.addRow(summaryRowData);
    worksheet.mergeCells(`A${summaryRowIndex}:E${summaryRowIndex}`);

    const labelCell = worksheet.getCell(summaryRowIndex, 1);
    labelCell.value = 'Over All Average Skill Set';
    labelCell.alignment = { horizontal: 'center', vertical: 'middle' };
    labelCell.font = {
      name: 'Calibri',
      size: 11,
      bold: true,
      color: { argb: 'FF000000' },
    };

    const summaryRow = worksheet.getRow(summaryRowIndex);
    summaryRow.height = 26;
    summaryRow.eachCell((cell, colNumber) => {
      cell.border = thinBorder;
      if (colNumber === 1) return;
      cell.font = {
        name: 'Calibri',
        size: 11,
        bold: true,
        color: { argb: 'FF000000' },
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      if (
        colNumber > baseColumns.length &&
        colNumber <= baseColumns.length + visibleSkills.length
      ) {
        cell.numFmt = '0.00';
      }
      if (colNumber === worksheet.columns.length) {
        cell.numFmt = '0.########';
      }
    });

    const headerRow = worksheet.getRow(1);
    headerRow.height = 90;
    headerRow.eachCell((cell, colNumber) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFC000' },
      };
      cell.font = {
        name: 'Calibri',
        size: 11,
        bold: true,
        color: { argb: 'FF000000' },
      };
      cell.border = thinBorder;

      const isSkillCol =
        colNumber > baseColumns.length &&
        colNumber <= baseColumns.length + visibleSkills.length;

      cell.alignment = {
        horizontal: 'center',
        vertical: 'middle',
        wrapText: true,
        textRotation: isSkillCol ? 90 : 0,
      };
    });

    worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: worksheet.columns.length },
    };
    worksheet.views = [{ state: 'frozen', xSplit: 5, ySplit: 1 }];

    /* --------------- Average Skills summary + chart sheet --------------- */
    const summarySheet = workbook.addWorksheet('Average Skills');
    summarySheet.columns = [
      { header: 'List Of Skills', key: 'label', width: 32 },
      { header: 'Average Skills', key: 'average', width: 18 },
    ];

    const summaryHeader = summarySheet.getRow(1);
    summaryHeader.height = 24;
    summaryHeader.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFC000' },
      };
      cell.font = {
        name: 'Calibri',
        size: 11,
        bold: true,
        color: { argb: 'FF000000' },
      };
      cell.border = thinBorder;
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });

    skillAverages.forEach((s) => {
      const r = summarySheet.addRow({
        label: s.label,
        average: s.average ? Number(s.average.toFixed(4)) : 0,
      });
      r.height = 20;
      r.eachCell((cell, colNumber) => {
        cell.border = thinBorder;
        cell.font = { name: 'Calibri', size: 11 };
        cell.alignment = {
          horizontal: colNumber === 1 ? 'left' : 'center',
          vertical: 'middle',
        };
      });
    });

    // Add a 1x1 transparent placeholder image. We don't actually want to
    // ship it — but doing so makes ExcelJS create the drawing scaffolding
    // (drawing1.xml, sheet→drawing relationship, drawing content type)
    // that we then hijack to point at a real native chart.
    const chartHasData = skillAverages.length > 0;
    if (chartHasData) {
      const placeholderId = workbook.addImage({
        base64: PLACEHOLDER_PNG_BASE64,
        extension: 'png',
      });
      summarySheet.addImage(placeholderId, {
        tl: { col: 3, row: 0 },
        ext: { width: 1, height: 1 },
      });
    }

    let outputBuffer = await workbook.xlsx.writeBuffer();

    // Replace the placeholder drawing with a native, editable column chart
    // bound to the data range on the "Average Skills" sheet.
    if (chartHasData) {
      try {
        outputBuffer = await injectNativeColumnChart(outputBuffer, {
          sheetName: 'Average Skills',
          title: 'Average Skills',
          valueLabel: 'Average Skills',
          color: '5B9BD5',
          rows: skillAverages.map((s) => ({
            label: s.label,
            value: Number((s.average || 0).toFixed(4)),
          })),
          max: 5,
          majorUnit: 0.5,
          fromCol: 3,
          fromRow: 0,
          toCol: 13,
          toRow: 20,
        });
      } catch (err) {
        // Native chart injection is best-effort — if anything goes wrong
        // we still ship the data sheet so the download is never blocked.
        console.warn('Could not inject native Excel chart:', err);
      }
    }

    saveAs(
      new Blob([outputBuffer]),
      `Competency_Matrix_${filterArr.month}_${filterArr.year}.xlsx`,
    );
  }, [
    filteredRows,
    visibleSkills,
    skillAverages,
    filterArr.month,
    filterArr.year,
  ]);

  if (permissionKnown && !canAccess) {
    return null;
  }

  return (
    <>
      <div className="d-md-flex align-items-center mb-2 flex-wrap gap-2">
        <div className="d-flex align-items-center flex-wrap gap-2">
          <h5 className="mb-0">Competency Matrix Overview</h5>
          <span className={style.skillMatrixUserPill}>
            <TeamOutlined />
            <span>All Users</span>
            <span className={style.skillMatrixUserPillCount}>
              {data.length}
            </span>
          </span>
        </div>
        <div className="ms-auto d-flex align-items-center flex-wrap gap-2">
          <div className="me-2" style={{ minWidth: 220 }}>
            <SearchInput
              key={searchInputKey}
              placeholder="Search by name, ID, or tag"
              value={filterArr.search}
              onSearchChange={handleSearchChange}
            />
          </div>

          <div className="mt-2 d-flex justify-content-between mt-md-0">
            <div>
              <Button
                type="dashed"
                className="me-1"
                htmlType="button"
                icon={<LuDownload size={18} />}
                onClick={handleDownload}
              >
                Download
              </Button>
            </div>
            <div>
              <Button
                title="Add Skill"
                type="primary"
                icon={<MdAdd size={20} />}
                onClick={openSkillModal}
                disabled={!buttonPermission?.add}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-2 mb-2">
        <TmFilter
          filterArray={competencyMatrixFilterArray}
          handleFilterChange={handleFilterChange}
          clearDatesOnReset
        />
      </div>

      {loading ? (
        <Loader />
      ) : (
        <>
          <TmMasterTable
            rows={tableRows}
            columns={competencyMatrixHeaders}
            custom={[]}
            bordered
          />

          <div className="row g-3 mt-3 mb-3">
            <div className="col-12 col-lg-5">
              <Card
                size="small"
                title={
                  <span className={style.competencySectionTitle}>
                    Average Skills Summary
                  </span>
                }
                className={style.competencyCard}
              >
                {summaryRows.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No skills to summarize"
                  />
                ) : (
                  <div className={style.competencySummaryTableWrap}>
                    <TmMasterTable
                      rows={summaryRows}
                      columns={summaryColumns}
                      custom={[]}
                      bordered
                    />
                  </div>
                )}
              </Card>
            </div>

            <div className="col-12 col-lg-7">
              <Card
                size="small"
                title={
                  <span className={style.competencySectionTitle}>
                    Average Skills Chart
                  </span>
                }
                className={style.competencyCard}
              >
                {skillAverages.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No data to plot"
                  />
                ) : (
                  <div className={style.competencyChartWrap}>
                    <Bar data={chartData} options={chartOptions} />
                  </div>
                )}
              </Card>
            </div>
          </div>
        </>
      )}

      <SkillManagerModal
        open={skillModalOpen}
        onClose={closeSkillModal}
        onSaved={handleSkillsSaved}
      />
    </>
  );
};

export default CompetencyMatrixComp;
